import { useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { PointCreateModalPages } from 'application/components/modals/PointCreateModal/types';
import { useBillingRepositoryInjector } from 'application/providers/DIContainerProvider';
import { ModalTypes } from 'application/providers/ModalProvider';
import AnalyticsService, {
  EventNameGTM,
  ParamNameAMP,
  ParamNameGTM,
  PointEventNameAMP,
  PointParamNameAMP,
  StreamBindFlowEventNameAMP,
} from 'application/services/AnalyticsService';
import CompanyUtils from 'application/utils/CompanyUtils';
import { useAccountContext } from 'application/providers/AccountProvider';
import { startAcceptanceAdvancedSigning } from 'application/store';
import { RouterUtils } from 'application/providers/RouterProvider';
import { INTEGRATION_PAGE_PATH } from 'application/consts';

import { Country, PaymentKind, UserFeatures } from 'domain/api/graphql/generated';

import { UseActionButtonsViewModel, UseActionButtonsViewModelProps } from './types';

const URI_FOR_RETURN = `${window.location.origin}/${RouterUtils.generatePath(INTEGRATION_PAGE_PATH, { type: 'sbbol' })}`;

const useActionButtonsViewModel = ({
  targetStreamId,
  collectionId,
  isTemplateMode,
  selectedPoints,
  currentPoint,
  isFullOnboarding,
  setCurrentPointStream,
  setInFlow,
  openModal,
  replaceModal,
  redirectToStreamCategoryPage,
  setIsStreamBindButtonLoading,
  showCopyStreamError,
  showCopyStreamWithThematicError,
  showCopyStreamWithThematicSuccess,
  copyTemplateStream,
  getSavedStreams,
  getStream,
  onboardingPassedRequest,
  refetchFeaturesOnboarding,
  readStreamWithThematic,
  handleChangeStream,
}: UseActionButtonsViewModelProps): UseActionButtonsViewModel => {
  const dispatch = useDispatch();

  const { currentCompany, companyIds } = useAccountContext();

  const { watchBillingInfo } = useBillingRepositoryInjector();

  const { canRealizeBillingOperation, hasInactiveLicenses, selectedPaymentKind } = watchBillingInfo(currentCompany!.id);

  const isDenyCreatePoint = CompanyUtils.isDenyCreatePoint(canRealizeBillingOperation, currentCompany);

  const handleAddPointClick = () => {
    AnalyticsService.event(PointEventNameAMP.POINT_CREATE_BUTTON_CLICK, {
      [PointParamNameAMP.FROM_PAGE]: PointCreateModalPages.STREAM,
    });

    setInFlow(!isDenyCreatePoint);

    if (isDenyCreatePoint) {
      replaceModal(ModalTypes.DENY_OPERATION, {});
    } else {
      replaceModal(ModalTypes.POINT_CREATE, {});
    }
  };

  const sendEvents = (streamId?: string) => {
    AnalyticsService.eventGTM(EventNameGTM.STREAM_START, {
      [ParamNameGTM.ACCOUNT_CREATED_DATE]: currentCompany?.created,
    });

    AnalyticsService.event(StreamBindFlowEventNameAMP.STREAM_BOUND, {
      [ParamNameAMP.STREAM_ID]: streamId,
      [ParamNameAMP.COLLECTION_ID]: collectionId,
    });
  };

  const onBindPointsClick = async () => {
    let streamId = targetStreamId;
    const withThematic = readStreamWithThematic();

    if (isTemplateMode) {
      try {
        streamId = (await copyTemplateStream(streamId!, currentCompany!.id, withThematic))!;

        if (withThematic) {
          showCopyStreamWithThematicSuccess();
        }
      } catch (error) {
        if (withThematic) {
          showCopyStreamWithThematicError();
          return '';
        }

        showCopyStreamError(currentCompany?.country || Country.Ru);
        return '';
      }
    }

    await handleChangeStream(streamId!);
    await getSavedStreams(companyIds);

    if (isTemplateMode) {
      redirectToStreamCategoryPage(streamId!);
    }

    sendEvents(streamId);

    if (targetStreamId) {
      await getStream(targetStreamId);
    }

    setCurrentPointStream({ id: streamId || '' });

    return streamId;
  };

  const bindStream = async () => {
    setIsStreamBindButtonLoading(true);

    const streamId = await onBindPointsClick();

    const isOk = await onboardingPassedRequest({ feature: UserFeatures.WarningStreamFirstStart });

    if (!isOk) {
      throw new Error();
    }

    await refetchFeaturesOnboarding();

    setIsStreamBindButtonLoading(false);

    return streamId;
  };

  const isShowPaymentModal = (streamId?: string) => {
    const hasDevice = Boolean(currentPoint?.device?.type);
    const hasStream = Boolean(currentPoint?.stream?.id) || Boolean(streamId);

    if (selectedPaymentKind === PaymentKind.Sbbol) {
      return hasInactiveLicenses && hasDevice && hasStream;
    }

    return currentPoint?.isEnabled === false && hasDevice && hasStream;
  };

  const handleBindStreamClick = useCallback(async () => {
    const streamId = await bindStream();

    if (!isFullOnboarding) {
      replaceModal(ModalTypes.STREAM_BIND_SUCCESS, {
        selectedPoints: selectedPoints ? [...selectedPoints.values()] : [],
      });

      return;
    }

    if (isShowPaymentModal(streamId)) {
      replaceModal(ModalTypes.PAYMENT_REQUIREMENT, {});
    } else {
      replaceModal(ModalTypes.STREAM_BIND_SUCCESS, {
        selectedPoints: selectedPoints ? [...selectedPoints.values()] : [],
      });
    }
  }, [bindStream, isShowPaymentModal, replaceModal]);

  const openAcceptanceAdvancedSigning = () => {
    dispatch(
      startAcceptanceAdvancedSigning({
        companyId: currentCompany!.id,
        uriForReturn: URI_FOR_RETURN,
        fallbackFn: () => {
          openModal(ModalTypes.SBERBUSSINESS_OPEN, {});
        },
      })
    );
  };

  return {
    handleAddPointClick,
    handleBindStreamClick,
    openAcceptanceAdvancedSigning,
  };
};

export default useActionButtonsViewModel;
